import { touch } from 'redux-form';
import { ThunkDispatch } from 'redux-thunk';

import {
  GO_TO_PAGE_APPLICATION,
  CREATE_PAGE_PROGRESS_MAP,
  UPDATE_PAGE_PROGRESS,
  REVIEW_APPLICATION_PAGE
} from './application.constants';
import { FORM_NAME } from '../../constants';

export const navigateToPageApplication = (pageNumber: number): NavigateToPageApplicationAction => {
  return {
    type: GO_TO_PAGE_APPLICATION,
    pageNumber
  };
};

export const createPageProgressMap = (
  resetPageProgress = false,
  pageFieldsIds: string[] = []
): CreatePageProgressMapAction => {
  return {
    type: CREATE_PAGE_PROGRESS_MAP,
    resetPageProgress,
    pageFieldsIds
  };
};

export const goToPageApplication = (pageNumber = 0) => {
  window.scroll(0, 0);
  return (dispatch: ThunkDispatch<ReduxState, {}, ReduxAction>) => {
    dispatch(navigateToPageApplication(pageNumber));
    dispatch(createPageProgressMap());
  };
};

export const updatePageProgress = (
  formSyncErrors: FormSyncErrors,
  pageNumber: number
): UpdatePageProgressAction => {
  return {
    type: UPDATE_PAGE_PROGRESS,
    formSyncErrors,
    pageNumber
  };
};

export const highlightMissingFields = () => {
  return (dispatch: ThunkDispatch<ReduxState, {}, ReduxAction>, getState: GetState) => {
    const {
      application: { progressMap, pageNumber }
    } = getState();

    const currentPageProgressMap = progressMap[pageNumber] || {};

    if (currentPageProgressMap.visited) {
      const fields = Object.keys(currentPageProgressMap.fields);
      dispatch(touch(FORM_NAME, ...fields));
    }
  };
};

export const reviewApplicationPage = (values: Values): ReviewApplicationPageAction => {
  return {
    type: REVIEW_APPLICATION_PAGE,
    values
  };
};

const initialState = {
  pageNumber: 0,
  progressMap: {},
  reviewingApplication: false,
  values: {}
};

export default function reducer(state: ApplicationState = initialState, action: ApplicationAction) {
  switch (action.type) {
    case GO_TO_PAGE_APPLICATION: {
      const { pageNumber } = action as NavigateToPageApplicationAction;
      // we mark the current page as already visited
      const { pageNumber: currentPage, progressMap } = state;

      const currentPageProgress = { ...progressMap[currentPage] };
      const updatedProgressMap = {
        ...progressMap,
        [currentPage]: {
          ...currentPageProgress,
          visited: true
        }
      };

      return {
        ...state,
        reviewingApplication: false,
        pageNumber,
        progressMap: updatedProgressMap
      };
    }
    case CREATE_PAGE_PROGRESS_MAP: {
      const { resetPageProgress, pageFieldsIds } = action as CreatePageProgressMapAction;
      const { progressMap, pageNumber } = state;

      if (progressMap[pageNumber] && !resetPageProgress) {
        return state;
      }
      // progress for this page doesn't exist
      const newProgress = { ...progressMap };
      const newFields = pageFieldsIds
        ? pageFieldsIds.reduce((acc: { [key: string]: string }, fieldId: string) => {
            acc[fieldId] = '';
            return acc;
          }, {})
        : {};
      const pageProgress = { fields: newFields, percentComplete: 0 };

      newProgress[pageNumber] = pageProgress;
      return { ...state, progressMap: newProgress };
    }
    case UPDATE_PAGE_PROGRESS: {
      const { formSyncErrors, pageNumber: actionPageNumber } = action as UpdatePageProgressAction;
      const { progressMap, pageNumber: statePageNumber } = state;

      // if action contains a pageNumber we use it, otherwise default to statePageNumber
      const currentPage = actionPageNumber || statePageNumber;

      if (!progressMap[currentPage]) {
        return state;
      }

      //progress for this page exists, so update it based on sync errors
      const pageProgress = { ...progressMap[currentPage] };
      Object.keys(pageProgress.fields).forEach(key => {
        //clear previous required values
        pageProgress.fields[key] = '';
      });

      if (formSyncErrors) {
        //set required fields based on sync errors
        Object.assign(pageProgress.fields, formSyncErrors);

        //if fields is empty object means we do not have errors
        pageProgress.percentComplete = Object.keys(pageProgress.fields).length
          ? ((Object.keys(pageProgress.fields).length - Object.keys(formSyncErrors).length) /
              Object.keys(pageProgress.fields).length) *
            100
          : 100;
      }

      const newProgressMap = {
        ...progressMap,
        [currentPage]: pageProgress
      };

      return {
        ...state,
        progressMap: newProgressMap
      };
    }
    case REVIEW_APPLICATION_PAGE: {
      const { values } = action as ReviewApplicationPageAction;

      return {
        ...state,
        reviewingApplication: true,
        values
      };
    }
    default:
      return state;
  }
}
